import React, {useState, useEffect} from 'react'
import {BuilderImage} from 'hec/types/common'
import {Dialog, DialogContent, DialogTitle, DialogDescription} from '@/components/ui/dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Button, routeTypes} from '@/components/ui/button'
import DOMPurify from 'dompurify'
import {Checkbox} from '@/components/ui/checkbox'

interface MarketingConsentCheckbox {
    show?: boolean
    value?: boolean
    label?: string
}

interface WelcomeModalProps {
    popupContent: string
    link?: string
    linkText?: string
    show: boolean
    isEdit: boolean
    logo?: BuilderImage
    marketingConsentCheckbox?: MarketingConsentCheckbox
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
    popupContent,
    link,
    linkText,
    show,
    isEdit,
    logo,
    marketingConsentCheckbox
}) => {
    const {site} = useMultiSite()
    const [open, setOpen] = useState<boolean>(false)
    const [marketingConsentChecked, setMarketingConsentChecked] = useState<boolean | null>(
        marketingConsentCheckbox?.value || null
    )

    useEffect(() => {
        const hasModalBeenShown = sessionStorage.getItem('welcomeModalShown')
        if (!hasModalBeenShown && show && !isEdit) {
            setOpen(true)
        }
    }, [show, isEdit])

    useEffect(() => {
        setMarketingConsentChecked(marketingConsentCheckbox?.value || true)
    }, [marketingConsentCheckbox?.value])

    const closeDialog = () => {
        let url = `/on/demandware.store/Sites-${String(
            site.id
        )}-Site/default/Login-DismissCardlessModal`
        if (marketingConsentChecked !== null) {
            url += `?marketingConsent=${String(marketingConsentChecked)}`
        }
        fetch(url).catch((e) => console.error('Dismiss Cardless Modal Error: ', e))
        sessionStorage.setItem('welcomeModalShown', 'true')
        setOpen(false)
    }
    const sanitizedPopupContent =
        marketingConsentCheckbox?.label && DOMPurify.sanitize(marketingConsentCheckbox.label)

    return (
        <Dialog open={open} onOpenChange={closeDialog} data-testid="welcomeModal-Dialog">
            <DialogContent
                className="max-w-[31.25rem] gap-0 px-[3.25rem] py-10"
                data-testid="welcomeModal-DialogContent"
            >
                <VisuallyHidden.Root>
                    <DialogTitle data-testid="welcomeModal-DialogTitle">Welcome popup</DialogTitle>
                    <DialogDescription data-testid="welcomeModal-DialogDescription">
                        TPA Welcome popup modal
                    </DialogDescription>
                </VisuallyHidden.Root>
                {logo?.src && (
                    <img
                        src={logo.src}
                        alt={logo?.altText || ''}
                        className="w-[15.25rem] mx-auto mb-8"
                    />
                )}
                <p
                    className="mb-5 font-grotesque bold text-[1.25rem] text-primary font-medium leading-6"
                    dangerouslySetInnerHTML={{
                        __html: popupContent
                    }}
                />

                {marketingConsentCheckbox && marketingConsentCheckbox.label && (
                    <div className="flex">
                        {sanitizedPopupContent && (
                            <label htmlFor="marketing-consent-checkbox" className="mb-4 flex ">
                                <Checkbox
                                    id="marketing-consent-checkbox"
                                    checked={marketingConsentChecked ?? undefined}
                                    onCheckedChange={(checked) => {
                                        setMarketingConsentChecked(!!checked)
                                    }}
                                    className="mb-4"
                                    data-testid="welcomeModal-MarketingConsentCheckbox"
                                />
                                {sanitizedPopupContent && (
                                    <div
                                        className="pl-2 font-grotesque text-[1rem] text-primary leading-6"
                                        dangerouslySetInnerHTML={{__html: sanitizedPopupContent}}
                                    />
                                )}
                            </label>
                        )}
                    </div>
                )}

                <button
                    className="w-full pt-3.5 pb-2.5 px-9 capitalize text-white font-grotesque font-medium text-sm leading-normal bg-[#ff295b] border-[#ff295b] rounded-[2.1875rem]"
                    onClick={closeDialog}
                >
                    Start Shopping
                </button>
                {link && linkText && (
                    <Button
                        ctaType="link"
                        variant="normal"
                        routeType={routeTypes.EXTERNAL}
                        hrefValue={link}
                        className="mt-6 text-base underline"
                        data-testid="welcomeModal-Button"
                    >
                        {linkText}
                    </Button>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default WelcomeModal
